import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(undefined);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [application, setApplication] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshUserData = async () => {
    const { data: { session: currentSession } } = await supabase.auth.getSession();

    if (currentSession?.user) {
      try {
        // Use limit(1) instead of maybeSingle() to handle multiple applications for same user
        const { data: appDataArray } = await supabase
          .from('applications')
          .select('*')
          .eq('user_id', currentSession.user.id)
          .order('applied_at', { ascending: false })
          .limit(1);
        
        const appData = appDataArray && appDataArray.length > 0 ? appDataArray[0] : null;

        const { data: profileData } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', currentSession.user.id)
          .maybeSingle();

        setApplication(appData);
        setProfile(profileData);
      } catch (error) {
        console.error('Error refreshing user data:', error);
      }
    } else {
      setApplication(null);
      setProfile(null);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      const { data: { session: currentSession } } = await supabase.auth.getSession();

      if (isMounted) {
        setSession(currentSession);
        setUser(currentSession?.user ?? null);

        if (currentSession?.user) {
          await refreshUserData();
        }
        setLoading(false);
      }
    };

    initializeAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, currentSession) => {
      if (isMounted) {
        setSession(currentSession);
        setUser(currentSession?.user ?? null);

        if (currentSession?.user) {
          (async () => {
            await refreshUserData();
          })();
        } else {
          setApplication(null);
          setProfile(null);
        }
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (!error && data?.user) {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('membership_status')
        .eq('user_id', data.user.id)
        .maybeSingle();

      if (profileData?.membership_status === 'suspended') {
        await refreshUserData();
        return { error: null, suspended: true };
      }

      await refreshUserData();
    }
    return { error, suspended: false };
  };

  const signUp = async (email, password) => {
    const { data, error } = await supabase.auth.signUp({ email, password });
    return { data, error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setApplication(null);
    setProfile(null);
  };

  const isAdmin = profile?.admin_role === true;
  const isPending = application?.status === 'pending';
  const isSuspended = profile?.membership_status === 'suspended';
  const hasAcceptedTerms = application?.terms_accepted === true && application?.privacy_accepted === true;
  const needsTermsAcceptance = user && application && !hasAcceptedTerms;

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        application,
        profile,
        loading,
        isAdmin,
        isPending,
        isSuspended,
        hasAcceptedTerms,
        needsTermsAcceptance,
        signIn,
        signUp,
        signOut,
        refreshUserData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
